/**
 * Electrosmith — Volume Pricing Price Display
 *
 * Watches the quantity input on product pages and shows a strikethrough base
 * price + discounted price when the quantity reaches a volume tier.
 *
 * Nothing is hardcoded any more. Everything comes from the JSON block that
 * snippets/es-volume-pricing-data.liquid outputs:
 *
 *   Base price  -> the currently selected variant's price
 *   Tier prices -> 1. the variant's catalog quantity price breaks, if configured
 *                  2. otherwise the product metafield custom.volume_tiers
 *
 * A product with neither source configured is left alone.
 *
 * custom.volume_tiers metafield (type: JSON), prices in the store currency:
 *   [
 *     { "min": 100,  "price": 33.99 },
 *     { "min": 300,  "price": 32.99 },
 *     { "min": 500,  "price": 31.99 },
 *     { "min": 1000, "price": 29.99 }
 *   ]
 * Each tier runs from its "min" up to the next tier's "min" (no "max" needed).
 *
 * Or give a fixed discount per item instead of a price, matching DealEasy's
 * "Fixed amount on each item" setting. It follows the variant price, so a
 * base price change needs no tier edits:
 *   [
 *     { "min": 100, "off": 1.67 },
 *     { "min": 300, "off": 3.34 },
 *     { "min": 500, "off": 5.00 }
 *   ]
 *
 * Or a percentage off, matching DealEasy's "Percentage" setting:
 *   [ { "min": 10, "percent": 5 }, { "min": 20, "percent": 10 } ]
 *
 * INSTALL:
 * 1. assets/electrosmith-volume-pricing.js      <- this file
 * 2. snippets/es-volume-pricing-data.liquid     <- the data snippet
 * 3. In sections/main-product.liquid, near the bottom:
 *      {% render 'es-volume-pricing-data', product: product %}
 *      <script src="{{ 'electrosmith-volume-pricing.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  const DATA_ID = 'es-volume-pricing-data';
  const WRAPPER_CLASS = 'es-volume-price';

  // ---------------------------------------------------------------------------
  // DATA
  // ---------------------------------------------------------------------------
  function readData() {
    const el = document.getElementById(DATA_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.warn('[es-volume-pricing] Could not parse pricing data:', err);
      return null;
    }
  }

  function getCurrency(data) {
    const s = window.Shopify && window.Shopify.currency;
    return {
      active: (s && s.active) || data.shopCurrency || 'USD',
      rate: (s && parseFloat(s.rate)) || 1,
    };
  }

  // Returns tiers as [{ min, price }] with price in cents, in the active currency.
  function getTiers(data, variant, currency) {
    // 1. Catalog quantity price breaks: already in the customer's currency.
    if (Array.isArray(variant.breaks) && variant.breaks.length) {
      return variant.breaks;
    }

    // 2. Product metafield: entered in the store currency.
    const mf = data.metafieldTiers;
    if (Array.isArray(mf) && mf.length) {
      // Outside the store currency, convert at Shopify's exchange rate.
      // This ignores per-market price adjustments (e.g. a +10% catalog
      // adjustment), so use catalog price breaks for exact foreign prices.
      const factor = currency.active === data.shopCurrency ? 1 : currency.rate;
      return mf
        .map((t) => ({
          min: Number(t.min),
          // "off" = fixed discount per item (mirrors DealEasy's "Fixed amount
          // on each item"), taken off the variant's live price.
          // "percent" = % off the variant's live price.
          // "price" = absolute tier price.
          price:
            t.off !== undefined
              ? variant.price - Math.round(Number(t.off) * 100 * factor)
              : t.percent !== undefined
              ? Math.round((variant.price * (100 - Number(t.percent))) / 100)
              : Math.round(Number(t.price) * 100 * factor),
        }))
        .filter((t) => Number.isFinite(t.min) && Number.isFinite(t.price));
    }

    return [];
  }

  // The tier with the highest "min" that the quantity has reached.
  function priceForQty(tiers, qty) {
    let best = null;
    for (const t of tiers) {
      if (qty >= t.min && (!best || t.min > best.min)) best = t;
    }
    return best ? best.price : null;
  }

  // ---------------------------------------------------------------------------
  // DOM LOOKUPS
  // Queried fresh on every update, because Dawn swaps out parts of the
  // product info (including the price) whenever the variant changes.
  // ---------------------------------------------------------------------------
  function getRoot() {
    return (
      document.querySelector('product-info[id^="MainProduct-"]') ||
      document.querySelector('product-info') ||
      null
    );
  }

  function getSelectedVariantId(root) {
    const input = root.querySelector(
      'product-form input[name="id"], form[action*="/cart/add"] [name="id"]'
    );
    return input ? String(input.value) : null;
  }

  // ---------------------------------------------------------------------------
  // FORMATTING
  // ---------------------------------------------------------------------------
  const formatters = {};
  function formatMoney(cents, currencyCode) {
    if (!formatters[currencyCode]) {
      try {
        formatters[currencyCode] = new Intl.NumberFormat(
          document.documentElement.lang || undefined,
          { style: 'currency', currency: currencyCode }
        );
      } catch (err) {
        formatters[currencyCode] = {
          format: (n) => currencyCode + ' ' + n.toFixed(2),
        };
      }
    }
    return formatters[currencyCode].format(cents / 100);
  }

  // ---------------------------------------------------------------------------
  // PRICE DISPLAY
  // Dawn renders the regular price inside .price__regular > .price-item--regular.
  // Sale prices (.price__sale) are left untouched.
  //
  // Only touches the DOM when something actually changes, so the
  // MutationObserver below doesn't loop on our own edits.
  // ---------------------------------------------------------------------------
  function renderPrice(priceContainer, baseCents, tierCents, currencyCode) {
    let wrapper = priceContainer.querySelector('.' + WRAPPER_CLASS);
    const original = priceContainer.querySelector('.price-item--regular');
    const show = tierCents !== null && tierCents < baseCents;

    if (show) {
      const key = currencyCode + '|' + baseCents + '|' + tierCents;

      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = WRAPPER_CLASS;
        wrapper.style.cssText =
          'display:flex; align-items:baseline; gap:8px; flex-wrap:wrap;';
        priceContainer.appendChild(wrapper);
      }

      if (wrapper.dataset.key !== key) {
        wrapper.dataset.key = key;
        wrapper.innerHTML = `
          <s class="price-item" style="color: var(--color-foreground); opacity: 0.45; font-size: 1em;">
            ${formatMoney(baseCents, currencyCode)}
          </s>
          <span class="price-item price-item--sale" style="color: rgb(var(--color-button)); font-weight: 700;">
            ${formatMoney(tierCents, currencyCode)}
          </span>
          <span style="font-size: 0.75em; opacity: 0.65; align-self: center;">each</span>
        `;
      }

      // Hide Dawn's own price so it isn't shown twice.
      if (original && original.style.display !== 'none') {
        original.style.display = 'none';
      }
    } else {
      if (wrapper) wrapper.remove();
      if (original && original.style.display === 'none') {
        original.style.display = '';
      }
    }
  }

  // ---------------------------------------------------------------------------
  // UPDATE LOOP
  // ---------------------------------------------------------------------------
  function update() {
    const root = getRoot();
    if (!root) return;

    const data = readData();
    if (!data || !data.variants) return;

    const priceContainer = root.querySelector('.price__regular');
    if (!priceContainer) return;

    const currency = getCurrency(data);
    const variantId = getSelectedVariantId(root);
    const variant = variantId && data.variants[variantId];

    if (!variant) {
      renderPrice(priceContainer, 0, null, currency.active); // restore Dawn's price
      return;
    }

    const qtyInput = root.querySelector('input[name="quantity"]');
    const qty = (qtyInput && parseInt(qtyInput.value, 10)) || 1;
    const tiers = getTiers(data, variant, currency);

    renderPrice(
      priceContainer,
      variant.price,
      priceForQty(tiers, qty),
      currency.active
    );
  }

  let scheduled = false;
  function scheduleUpdate() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      update();
    });
  }

  // ---------------------------------------------------------------------------
  // INIT
  // ---------------------------------------------------------------------------
  function init() {
    if (!document.getElementById(DATA_ID) || !getRoot()) return;

    // Quantity typing, +/- buttons and variant pickers all fire bubbling
    // input/change events. Listening on the document survives Dawn
    // replacing those elements.
    const onEvent = (e) => {
      const root = getRoot();
      if (root && root.contains(e.target)) scheduleUpdate();
    };
    document.addEventListener('input', onEvent);
    document.addEventListener('change', onEvent);

    // After a variant change, Dawn fetches fresh HTML and replaces the price,
    // which removes our wrapper. Re-apply whenever that happens.
    const section = getRoot().closest('.shopify-section') || document.body;
    new MutationObserver(scheduleUpdate).observe(section, {
      childList: true,
      subtree: true,
    });

    update();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();